// OCR preprocessing — canvas-based image cleanup to boost Tesseract accuracy.
// Exposes: window.OcrPre = { process(imageData, opts), autoCrop, toGray, otsu, rotate }
(function () {
  'use strict';

  function toGray(src) {
    const { width: w, height: h, data: d } = src;
    const out = new Uint8ClampedArray(w * h * 4);
    for (let i = 0; i < d.length; i += 4) {
      const g = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0;
      out[i] = out[i + 1] = out[i + 2] = g;
      out[i + 3] = 255;
    }
    return new ImageData(out, w, h);
  }

  // Otsu's threshold for adaptive binarization
  function otsuThreshold(data, w, h) {
    const hist = new Array(256).fill(0);
    for (let i = 0; i < data.length; i += 4) hist[data[i]]++;
    const total = w * h;
    let sum = 0;
    for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0, wB = 0, wF, mB, mF, max = 0, thr = 128;
    for (let t = 0; t < 256; t++) {
      wB += hist[t]; if (!wB) continue;
      wF = total - wB; if (!wF) break;
      sumB += t * hist[t];
      mB = sumB / wB; mF = (sum - sumB) / wF;
      const between = wB * wF * (mB - mF) ** 2;
      if (between > max) { max = between; thr = t; }
    }
    return thr;
  }

  function binarize(src) {
    const gray = toGray(src);
    const thr = otsuThreshold(gray.data, gray.width, gray.height);
    const out = new Uint8ClampedArray(gray.data.length);
    for (let i = 0; i < gray.data.length; i += 4) {
      const v = gray.data[i] < thr ? 0 : 255;
      out[i] = out[i + 1] = out[i + 2] = v;
      out[i + 3] = 255;
    }
    return new ImageData(out, gray.width, gray.height);
  }

  // ── Sauvola ADAPTIVE binarization (integral images) ──────────────────────
  // Ported from document-scanner/cv-enhance.js — the local-threshold method
  // that survives shadows and uneven lighting where global Otsu collapses
  // (battery 2026-07-02: shadowed phone photo → Tesseract 17% conf, 2 words;
  // Sauvola is the standard rescue for exactly that input class).
  function sauvolaBinarize(src, opts) {
    opts = opts || {};
    const { width: w, height: h, data: d } = src;
    // grayscale plane
    const g = new Float32Array(w * h);
    for (let i = 0, p = 0; i < d.length; i += 4, p++) g[p] = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
    const win = (opts.win || Math.max(15, Math.round(Math.min(w, h) / 36))) | 1;
    const k = opts.k != null ? opts.k : 0.2, R = 128, r = win >> 1;
    const II = integral(g, w, h);
    const sq = new Float32Array(w * h); for (let i = 0; i < g.length; i++) sq[i] = g[i] * g[i];
    const II2 = integral(sq, w, h);
    const out = new Uint8ClampedArray(d.length);
    let inkCount = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const x0 = Math.max(0, x - r), y0 = Math.max(0, y - r), x1 = Math.min(w, x + r + 1), y1 = Math.min(h, y + r + 1);
        const n = (x1 - x0) * (y1 - y0);
        const s = boxSum(II, w, x0, y0, x1, y1), s2 = boxSum(II2, w, x0, y0, x1, y1);
        const mean = s / n, varr = Math.max(0, s2 / n - mean * mean);
        const thr = mean * (1 + k * (Math.sqrt(varr) / R - 1));
        const idx = y * w + x, o = idx * 4, val = g[idx] > thr ? 255 : 0;
        if (val === 0) inkCount++;
        out[o] = out[o + 1] = out[o + 2] = val; out[o + 3] = 255;
      }
    }
    // Invert guard: light-text-on-dark input (dark-mode screenshots) comes out
    // as a mostly-black page — flip it so Tesseract sees dark-on-light.
    if (inkCount > (w * h) * 0.5) {
      for (let i = 0; i < out.length; i += 4) {
        const v = 255 - out[i];
        out[i] = out[i + 1] = out[i + 2] = v;
      }
    }
    return new ImageData(out, w, h);
  }
  function integral(plane, w, h) {
    const II = new Float64Array((w + 1) * (h + 1));
    for (let y = 1; y <= h; y++) { let rs = 0; for (let x = 1; x <= w; x++) { rs += plane[(y - 1) * w + (x - 1)]; II[y * (w + 1) + x] = II[(y - 1) * (w + 1) + x] + rs; } }
    return II;
  }
  function boxSum(II, w, x0, y0, x1, y1) { const W1 = w + 1; return II[y1 * W1 + x1] - II[y0 * W1 + x1] - II[y1 * W1 + x0] + II[y0 * W1 + x0]; }

  // Decide whether a page needs ADAPTIVE binarization before OCR. Cheap: runs
  // on a ≤240px downscale. True when (a) global contrast is weak (washed-out
  // photo) or (b) the background brightness varies a lot across quadrants
  // (shadow gradient). Clean flatbed scans (high contrast, uniform paper) skip
  // it — protects the already-green baseline battery cases from regressions.
  function needsAdaptiveBinarize(src) {
    const w = src.width, h = src.height;
    const scale = Math.max(1, Math.ceil(Math.max(w, h) / 240));
    const sw = Math.max(8, Math.floor(w / scale)), sh = Math.max(8, Math.floor(h / scale));
    const lum = new Float32Array(sw * sh);
    const d = src.data;
    for (let y = 0; y < sh; y++) {
      for (let x = 0; x < sw; x++) {
        const i = ((y * scale) * w + (x * scale)) * 4;
        lum[y * sw + x] = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
      }
    }
    // Ink-to-paper contrast: p99.5 (paper) − p0.5 (darkest ink). p5 was wrong
    // here — on sparse-ink pages (fax, thin tables: <5% ink pixels) p5 equals
    // the paper level, contrast computed as ≈0 and Sauvola fired on perfectly
    // binary images, destroying them (battery: lowdpi-fax 2/2 → 0/2). p0.5
    // still reaches the ink even when it covers only 1% of the page.
    const sorted = Float32Array.from(lum).sort();
    const pLo = sorted[(sorted.length * 0.005) | 0], pHi = sorted[Math.min(sorted.length - 1, (sorted.length * 0.995) | 0)];
    const globalContrast = pHi - pLo;
    // background level per quadrant = p80 of that quadrant (paper, not ink)
    const q = (x0, y0, x1, y1) => {
      const vals = [];
      for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) vals.push(lum[y * sw + x]);
      vals.sort((a, b) => a - b);
      return vals[(vals.length * 0.8) | 0] || 0;
    };
    const hw = sw >> 1, hh = sh >> 1;
    const bgs = [q(0, 0, hw, hh), q(hw, 0, sw, hh), q(0, hh, hw, sh), q(hw, hh, sw, sh)];
    const bgSpread = Math.max.apply(null, bgs) - Math.min.apply(null, bgs);
    return globalContrast < 110 || bgSpread > 25;
  }

  function invert(src) {
    const out = new Uint8ClampedArray(src.data.length);
    for (let i = 0; i < src.data.length; i += 4) {
      out[i] = 255 - src.data[i];
      out[i + 1] = 255 - src.data[i + 1];
      out[i + 2] = 255 - src.data[i + 2];
      out[i + 3] = 255;
    }
    return new ImageData(out, src.width, src.height);
  }

  // Box blur (simple denoise) — 3x3 neighbourhood mean
  function boxBlur(src, r) {
    r = r || 1;
    const { width: w, height: h, data: d } = src;
    const out = new Uint8ClampedArray(d.length);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let r0 = 0, g0 = 0, b0 = 0, n = 0;
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            const i = (ny * w + nx) * 4;
            r0 += d[i]; g0 += d[i + 1]; b0 += d[i + 2]; n++;
          }
        }
        const o = (y * w + x) * 4;
        out[o] = r0 / n; out[o + 1] = g0 / n; out[o + 2] = b0 / n; out[o + 3] = 255;
      }
    }
    return new ImageData(out, w, h);
  }

  // Unsharp mask: orig + (orig - blurred) * amount
  function sharpen(src, amount) {
    amount = amount == null ? 0.8 : amount;
    const blurred = boxBlur(src, 1);
    const out = new Uint8ClampedArray(src.data.length);
    for (let i = 0; i < src.data.length; i += 4) {
      for (let c = 0; c < 3; c++) {
        const v = src.data[i + c] + (src.data[i + c] - blurred.data[i + c]) * amount;
        out[i + c] = v < 0 ? 0 : v > 255 ? 255 : v;
      }
      out[i + 3] = 255;
    }
    return new ImageData(out, src.width, src.height);
  }

  // Detect skew angle via horizontal projection variance (cheap heuristic).
  // Returns angle in degrees between -10 and 10. Zero if confident enough or flat.
  function detectSkew(src) {
    const gray = toGray(src);
    const { width: w, height: h, data: d } = gray;
    // Shrink for speed
    const scale = Math.max(1, Math.floor(w / 400));
    const sw = Math.floor(w / scale), sh = Math.floor(h / scale);
    const small = new Uint8Array(sw * sh);
    for (let y = 0; y < sh; y++) {
      for (let x = 0; x < sw; x++) {
        small[y * sw + x] = d[(y * scale * w + x * scale) * 4];
      }
    }
    // Test angles from -8° to 8° in 0.5° steps
    let bestAngle = 0, bestScore = -Infinity;
    for (let a = -8; a <= 8; a += 0.5) {
      const rad = a * Math.PI / 180;
      const tan = Math.tan(rad);
      const row = new Float32Array(sh);
      for (let y = 0; y < sh; y++) {
        let sum = 0, cnt = 0;
        for (let x = 0; x < sw; x++) {
          const sy = Math.round(y + tan * (x - sw / 2));
          if (sy < 0 || sy >= sh) continue;
          sum += small[sy * sw + x] < 128 ? 1 : 0; cnt++;
        }
        row[y] = cnt ? sum / cnt : 0;
      }
      // Variance of row density — higher = sharper text lines = correct angle
      let mean = 0; for (let y = 0; y < sh; y++) mean += row[y]; mean /= sh;
      let v = 0; for (let y = 0; y < sh; y++) v += (row[y] - mean) ** 2; v /= sh;
      if (v > bestScore) { bestScore = v; bestAngle = a; }
    }
    return Math.abs(bestAngle) < 0.7 ? 0 : bestAngle;
  }

  // Rotate ImageData by angle (degrees). Returns new ImageData on an offscreen canvas.
  // `fill` (optional CSS color): what the uncovered corners become. Default
  // white is right for OCR scans; a phone PHOTO has grey paper (~200), and a
  // white wedge against it reads as a long dark edge to anything that looks
  // for lines relative to local paper (omr-staff-detect) — pass the paper
  // colour there.
  function rotate(src, angle, fill) {
    const rad = angle * Math.PI / 180;
    const cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
    const nw = Math.ceil(src.width * cos + src.height * sin);
    const nh = Math.ceil(src.width * sin + src.height * cos);
    const canvas = document.createElement('canvas');
    canvas.width = nw; canvas.height = nh;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = fill || '#fff'; ctx.fillRect(0, 0, nw, nh);
    ctx.translate(nw / 2, nh / 2);
    ctx.rotate(rad);
    const tmp = document.createElement('canvas');
    tmp.width = src.width; tmp.height = src.height;
    tmp.getContext('2d').putImageData(src, 0, 0);
    ctx.drawImage(tmp, -src.width / 2, -src.height / 2);
    return ctx.getImageData(0, 0, nw, nh);
  }

  // Find non-white content bounding box (for autocrop)
  function autoCrop(src, threshold) {
    threshold = threshold == null ? 240 : threshold;
    const { width: w, height: h, data: d } = src;
    let minX = w, minY = h, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < h; y += 2) {
      for (let x = 0; x < w; x += 2) {
        const i = (y * w + x) * 4;
        const g = (d[i] + d[i + 1] + d[i + 2]) / 3;
        if (g < threshold) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }
    if (!found) return src;
    const pad = 12;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);
    const cw = maxX - minX + 1, ch = maxY - minY + 1;
    if (cw < w * 0.5 || ch < h * 0.5) {
      // Only crop if it removes a meaningful margin; otherwise keep original
      const canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      const src2 = document.createElement('canvas');
      src2.width = w; src2.height = h;
      src2.getContext('2d').putImageData(src, 0, 0);
      canvas.getContext('2d').drawImage(src2, -minX, -minY);
      return canvas.getContext('2d').getImageData(0, 0, cw, ch);
    }
    return src;
  }

  // Main pipeline — takes options and applies filters in sensible order
  function process(src, opts) {
    opts = opts || {};
    let img = src;
    if (opts.auto) {
      // Autocrop big margins, then RESCUE hard photos with Sauvola when the
      // heuristic says the page is washed-out or shadow-graded (adaptive
      // binarization first makes the projection-based deskew far more
      // reliable on those same pages), deskew, and finally a gentle sharpen —
      // skipped after binarization (sharpening a 1-bit image only adds halos).
      //
      // opts.neural — the page goes to the neural recogniser (PP-OCRv5), which
      // reads grey and colour as they are: Sauvola and sharpening were
      // Tesseract's needs, and measured 12.09.2026 they HURT the other engine
      // (a dark UI screenshot: 100 % CER binarised, 0 % untouched). Only the
      // geometry survives — margins and skew are what a line-crop recogniser
      // still pays for.
      const cropped = autoCrop(img, 245);
      if (cropped !== img) img = cropped;
      let binarized = false;
      if (!opts.neural && needsAdaptiveBinarize(img)) { img = sauvolaBinarize(img); binarized = true; }
      const skew = detectSkew(img);
      if (Math.abs(skew) >= 1) img = rotate(img, -skew);
      if (!binarized && !opts.neural) img = sharpen(img, 0.4);
      return img;
    }
    if (opts.autocrop) img = autoCrop(img);
    if (opts.deskew) {
      const angle = detectSkew(img);
      if (Math.abs(angle) >= 0.5) img = rotate(img, -angle);
    }
    if (opts.denoise) img = boxBlur(img, 1);
    if (opts.sharpen) img = sharpen(img, 0.8);
    // Manual binarize now uses Sauvola too — adaptive strictly dominates the
    // old global Otsu on real captures (Otsu remains exported for tests/tools).
    if (opts.binarize) img = sauvolaBinarize(img);
    if (opts.invert) img = invert(img);
    return img;
  }

  window.OcrPre = { process, toGray, binarize, sauvolaBinarize, needsAdaptiveBinarize, invert, sharpen, detectSkew, rotate, autoCrop, boxBlur, otsuThreshold };
})();
